import { useAxiosWithAuth } from 'api/services/useAxios';
import {
  CustomAttributeEntity,
  UnionCustomAttributeProps
} from 'components/common/Portals/CustomAttributes/types';
import { baseApi } from 'components/constants/Constants';
import {
  IngredientListRow,
  PrivatePortalFacetsResponse
} from 'types/BrandPortal/Ingredients';
import { SegmentTag } from 'types/global';
import { TablePage } from 'types/Table';
import { usePaginatedAxios } from '../usePaginatedAxios';

interface IngredientsPortalResponse {
  facets: PrivatePortalFacetsResponse[];
  content: IngredientListRow[];
  page: TablePage;
}

export const useGetNewProducts = (eventKey: string) =>
  useAxiosWithAuth<IngredientsPortalResponse>(
    {
      url: `${baseApi}/portals/${eventKey}/products`,
      method: 'GET'
    },
    { manual: true }
  );

export type AvailableProduct = {
  company: {
    key: string;
    name: string;
  };
  key: string;
  name: string;
  segment: SegmentTag;
};

type GetAvailableProductsResponse = {
  content: AvailableProduct[];
  page: TablePage;
};

export const useGetAvailableProducts = (eventKey: string) =>
  useAxiosWithAuth<GetAvailableProductsResponse>(
    {
      url: `${baseApi}/events/${eventKey}/products/available`,
      method: 'GET'
    },
    { manual: true }
  );

export interface LabelResponse {
  id: string;
  title: string;
  color: string;
  reference: number;
  description: string;
  count: number;
}

export const useGetPortalCustomAttributesDefinitionsOptionsPaginated = (
  eventKey: string,
  definitionKey: string,
  type: CustomAttributeEntity
) => {
  const result = usePaginatedAxios<{
    key: string;
    value: string;
    color: string;
  }>(
    {
      url: `${baseApi}/portals/${eventKey}/${type}/attribute-definitions/${definitionKey}/options?sort=value.label,asc`,
      method: 'GET'
    },
    ['value.label__icontains']
  );

  return result;
};

export const useGetPortalCustomAttributesDefinitions = (
  eventKey: string,
  type: CustomAttributeEntity,
  manual?: boolean
) =>
  useAxiosWithAuth<UnionCustomAttributeProps[]>(
    {
      url: `${baseApi}/portals/${eventKey}/${type}/attribute-definitions?sort=value.label,asc`,
      method: 'GET'
    },
    {
      manual
    }
  );

export const usePatchPortalCustomAttributeDefinitionsToProduct = (
  portal: string,
  company: string,
  segment: SegmentTag,
  product: string
) => {
  return useAxiosWithAuth<
    null,
    { definition: string; value: string | string[] | null }[]
  >(
    {
      url: `${baseApi}/portals/${portal}/companies/${company}/segments/${segment}/products/${product}/custom-attributes`,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      }
    },
    {
      manual: true
    }
  );
};

export const useDeletePortalIngredientAttributes = (
  portal: string,
  company: string,
  product: string,
  segment: string
) =>
  useAxiosWithAuth(
    {
      url: `${baseApi}/portals/${portal}/companies/${company}/segments/${segment}/products/${product}/custom-attributes`,
      method: 'DELETE'
    },
    { manual: true }
  );
